import { DateTime } from "luxon"
import Constants from "../../../api/Constants"
import { ClassConfiguration } from "../../../api/model/Display"
import TimetableSchedule, { ScheduleLesson } from "../../../api/model/Timetable"
import DateUtilities from "../../../utility/DateUtilities"
import StringUtilities from "../../../utility/StringUtilities"

class TimetableConfiguration {
  private configuration: ClassConfiguration[]
  private classLocationFilter: string[] | undefined
  private timetableDefaultExcludedRooms: string[] | undefined
  private reservationCourseTokens: string[]

  private school: string

  constructor(
    classConfiguration: ClassConfiguration[],
    classLocationFilter: string[] | undefined,
    school: string,
    timetableDefaultExcludedRooms: string[] | undefined,
    reservationCourseTokens?: string[],
  ) {
    this.configuration = classConfiguration
    this.classLocationFilter = classLocationFilter
    this.school = school
    this.timetableDefaultExcludedRooms = timetableDefaultExcludedRooms
    this.reservationCourseTokens = reservationCourseTokens ?? []
  }

  private defaultConfiguration(at: DateTime) {
    if (this.school === "gbw") {
      let configurations: ClassConfiguration[] = []
      Constants.ContentDisplayTimes.GBW_TIMETABLE_DEFAULT_CONFIGS.forEach((defaultConfig) => {
        if (DateUtilities.isDateAfterTime(at, defaultConfig.start))
          configurations = defaultConfig.configurations
      })
      return configurations
    }
    return []
  }

  public schedulers(from: TimetableSchedule, at: DateTime) {
    const configuration = this.hasAnyRestrictions
      ? this.configuration
      : this.defaultConfiguration(at)

    return configuration.map((config) => {
      const slicedConfiguration = new TimetableConfiguration(
        [config],
        this.classLocationFilter,
        this.school,
        this.timetableDefaultExcludedRooms,
        this.reservationCourseTokens,
      )
      return {
        configuration: config,
        schedule: slicedConfiguration.filteredSchedule(from),
        times: TimetableConfiguration.timesForConfiguration(config),
      }
    })
  }

  public filteredSchedule(from: TimetableSchedule) {
    const schedule: TimetableSchedule = {}
    for (const className in from) {
      if (!from.hasOwnProperty(className)) continue

      const isAllowed = !this.hasAnyRestrictions || this.isClassIncluded(className, from)
      if (isAllowed) {
        // Filter individual lessons within the class schedule
        const filteredClassSchedule = this.filterLessonsInClassSchedule(from[className])
        // Only include the class if it has lessons after filtering
        if (Object.keys(filteredClassSchedule).length > 0) {
          schedule[className] = filteredClassSchedule
        }
      }
    }
    return schedule
  }

  private filterLessonsInClassSchedule(classSchedule: { [time: string]: ScheduleLesson[] }) {
    const filtered: { [time: string]: ScheduleLesson[] } = {}

    for (const time in classSchedule) {
      if (!classSchedule.hasOwnProperty(time)) continue

      const filteredLessons = classSchedule[time].filter((lesson) => {
        // Check if room should be excluded by default
        const isExcludedRoom = this.timetableDefaultExcludedRooms?.some((excludedRoom) =>
          lesson.room.startsWith(excludedRoom),
        )
        // If room is excluded, check if it's explicitly included in classLocationFilter
        if (isExcludedRoom) {
          const isExplicitlyIncluded = this.classLocationFilter?.some((location) =>
            lesson.room.startsWith(location),
          )
          return isExplicitlyIncluded === true
        }
        // If not excluded, apply normal location filter logic
        if (this.classLocationFilter?.length) {
          return this.classLocationFilter.some((location) => lesson.room.startsWith(location))
        }
        return true
      })
      // Only include time slots that have lessons after filtering
      if (filteredLessons.length > 0) {
        filtered[time] = filteredLessons
      }
    }
    return filtered
  }

  public get hasAnyRestrictions() {
    return this.configuration.length > 0
  }

  public isClassIncluded(className: string, from: TimetableSchedule) {
    return (
      this.configuration.some((configuration) =>
        this.doesClassNameMatchConfiguration(className, configuration),
      ) && this.isClassContextuallyRelevant(className, from[className])
    )
  }

  private isClassContextuallyRelevant(
    _className: string,
    classSchedule: { [time: string]: ScheduleLesson[] },
  ) {
    const lessons = Object.values(classSchedule).reduce((a, b) => a.concat(b), [])
    if (!lessons.length) return false

    // Filter lessons based on room exclusions and location filters
    const relevantLessons = lessons.filter((lesson) => {
      // Check if room should be excluded by default
      const isExcludedRoom = this.timetableDefaultExcludedRooms?.some((excludedRoom) =>
        lesson.room.startsWith(excludedRoom),
      )

      // If room is excluded, check if it's explicitly included in classLocationFilter
      if (isExcludedRoom) {
        const isExplicitlyIncluded = this.classLocationFilter?.some((location) =>
          lesson.room.startsWith(location),
        )
        return isExplicitlyIncluded === true
      }

      // If not excluded, apply normal location filter logic
      if (this.classLocationFilter?.length) {
        return this.classLocationFilter.some((location) => lesson.room.startsWith(location))
      }

      return true
    })

    return relevantLessons.length > 0
  }

  private static timesForConfiguration(configuration: ClassConfiguration) {
    switch (configuration) {
      case ClassConfiguration.ShowBaseEducationClassesGBW:
        return Constants.TimetableTimes.GBW_GB
      case ClassConfiguration.ShowFurtherEducationClassesGBW:
        return Constants.TimetableTimes.GBW_WB
      default:
        return Constants.TimetableTimes.GBW_WB
    }
  }

  public highlightForClass(className: string) {
    if (this.school === "gbw") {
      const startsWithFurtherEducationPrefix = StringUtilities.startsWithAny(
        className,
        Constants.ClassConfigurationPrefixes.GBW_FURTHER_EDUCATION,
      )
      return startsWithFurtherEducationPrefix ? "turquoise" : null
    }
    return null
  }

  private doesClassNameMatchConfiguration = (
    className: string,
    configuration: ClassConfiguration,
  ) => {
    if (this.school === "gbw") {
      if (this.reservationCourseTokens.includes(className)) {
        return (
          !configuration ||
          configuration === ClassConfiguration.Any ||
          configuration === ClassConfiguration.ShowFurtherEducationClassesGBW
        )
      }
      const startsWithFurtherEducationPrefix = StringUtilities.startsWithAny(
        className,
        Constants.ClassConfigurationPrefixes.GBW_FURTHER_EDUCATION,
      )
      if (!configuration || configuration === ClassConfiguration.Any) {
        return true
      } else if (configuration === ClassConfiguration.ShowFurtherEducationClassesGBW) {
        return startsWithFurtherEducationPrefix
      } else {
        return !startsWithFurtherEducationPrefix
      }
    }
    return false
  }
}

export default TimetableConfiguration
